import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { isMobile } from "react-device-detect";
import logoImage from "../../../../assets/images/logo.png";
import logoWebpay from "../../../../assets/images/webpay.png";
import invokeApi from "../../../../services/invokeApi";

const LoaderOverlay = ({ isLoading }) => {
  if (!isLoading) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
    >
      <CircularProgress sx={{ color: "#3B82F6" }} />
    </Box>
  );
};

const BlueCheckbox = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.grey[400],
  "&.Mui-checked": {
    color: "#1976d2",
  },
  "& .MuiSvgIcon-root": {
    fontSize: 28,
  },
}));

export default function PagoSesionPendiente() {
  const { id_cobro_sesion } = useParams();
  const [loading, setLoading] = useState(true);
  const [cobroData, setCobroData] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString + "T00:00:00");
    const days = [
      "domingo",
      "lunes",
      "martes",
      "miércoles",
      "jueves",
      "viernes",
      "sábado",
    ];
    const months = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];

    const dayName = days[date.getDay()];
    const day = date.getDate().toString().padStart(2, "0");
    const month = months[date.getMonth()];

    return `${dayName} ${day} de ${month}`;
  };

  const formatTimestamp = (timestampString) => {
    if (!timestampString) return "";

    const date = new Date(timestampString);
    const days = [
      "domingo",
      "lunes",
      "martes",
      "miércoles",
      "jueves",
      "viernes",
      "sábado",
    ];
    const months = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];

    const dayName = days[date.getDay()];
    const day = date.getDate().toString().padStart(2, "0");
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const amPm = hours < 12 ? "AM" : "PM";

    return `${dayName} ${day} de ${month} del ${year} a las ${hours.toString().padStart(2, "0")}:${minutes} ${amPm}`;
  };

  const transformModalidad = (modalidad) => {
    switch (modalidad) {
      case "consultorio":
        return "Consulta presencial";
      case "domicilio":
        return "Consulta a domicilio";
      case "online":
        return "Consulta online";
      case "telemedicina":
        return "Consulta online";
      default:
        return modalidad;
    }
  };

  const formatearPrecio = (valor) => {
    if (!valor) return "0";
    return parseInt(valor).toLocaleString("es-CL");
  };

  const getTituloPagina = (estado) => {
    switch (estado) {
      case "pendiente_pago":
        return "Pagar Sesión Pendiente";
      case "pagado":
        return "Estado de la Sesión";
      case "no_cobrable":
        return "Estado de la Sesión";
      default:
        return "Información de la Sesión";
    }
  };

  const getTextoEncabezado = (estado) => {
    return estado === "pendiente_pago"
      ? "Estás por pagar con:"
      : "Información de la sesión con:";
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id_cobro_sesion) {
        setError("ID de cobro no válido");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await invokeApi(
          `/agenda/linkPagoInformacion?id_cobro_sesion=${id_cobro_sesion}`
        );

        if (result.error) {
          setError(result.error.message || "Error al cargar la información");
        } else if (result.statusCode === 200) {
          setCobroData(result);
        } else {
          setError("No se pudo cargar la información de la sesión");
        }
      } catch (err) {
        console.error("Error fetching cobro data:", err);
        setError("Error al cargar la información. Por favor, intenta más tarde.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id_cobro_sesion]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePagar = async () => {
    console.log("Iniciando proceso de pago para:", id_cobro_sesion);
    setIsProcessing(true);
    
    try {
      const result = await invokeApi(
        '/agenda/linkPagoCrear',
        'post',
        { id_cobro_sesion: id_cobro_sesion }
      );

      if (result.statusCode === 200 && result.transbank) {
        const webpayUrl = `${result.transbank.url}?token_ws=${result.transbank.token}`;
        
        console.log("Redirigiendo a Webpay:", webpayUrl);
        
        window.location.href = webpayUrl;
      } else if (result.error) {
        throw new Error(result.error.message);
      } else {
        throw new Error('Respuesta inválida del servidor');
      }
    } catch (err) {
      console.error("Error al crear transacción de pago:", err);
      setIsProcessing(false);
      
      const errorMessage = err.message || 'Error desconocido';
      
      if (errorMessage.includes('ya fue pagada')) {
        alert('Esta sesión ya fue pagada. Por favor, recarga la página para ver el estado actualizado.');
      } else if (errorMessage.includes('no es cobrable')) {
        alert('Esta sesión no puede ser cobrada en este momento. Contacta con el profesional si crees que es un error.');
      } else if (errorMessage.includes('Error al crear transacción Webpay')) {
        alert('Hubo un problema al comunicarse con el sistema de pago. Por favor, intenta nuevamente en unos momentos.');
      } else {
        alert('No se pudo procesar el pago. Por favor, verifica tu conexión e intenta nuevamente.');
      }
    }
  };

  const renderContenidoCajaBlanca = () => {
    if (!cobroData) return null;

    const { cobro, sesion, paciente, profesional } = cobroData;
    const estadoCobro = cobro?.estado_cobro;

    if (estadoCobro === "pendiente_pago") {
      return (
        <>
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              color: "#191C1F",
              textAlign: "left",
              lineHeight: 1.6,
              mb: 3,
            }}
          >
            Hola{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {paciente?.nombres} {paciente?.apellidos}
            </Box>
            ,
            <br />
            <br />
            Tu {profesional?.profesion?.toLowerCase() || "profesional"}{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {profesional?.nombre}
            </Box>{" "}
            te solicita el pago de tu sesión del{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {formatDate(sesion?.fecha)}
            </Box>{" "}
            a las{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {sesion?.hora}
            </Box>{" "}
            por un monto de{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              ${formatearPrecio(cobro?.valor_cobro)}
            </Box>
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              paddingTop: 1,
              paddingBottom: 1,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              width: "100%",
              cursor: "pointer",
              backgroundColor: "#f5f5f5",
            }}
            onClick={() => {}}
          >
            <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
              <img
                src={logoWebpay}
                alt="Webpay Logo"
                style={{ height: "24px", marginRight: "12px" }}
              />
              <Typography
                sx={{
                  fontFamily: "Quicksand-Regular",
                  fontSize: { md: "16px", xs: "14px" },
                  color: "#191C1F",
                }}
              >
                Tarjeta de crédito o débito
              </Typography>
            </Box>
            <BlueCheckbox
              checked={true}
              onClick={(e) => e.stopPropagation()}
              sx={{ mr: 1 }}
            />
          </Box>

          <Button
            onClick={handlePagar}
            disabled={isProcessing}
            disableRipple
            disableElevation
            disableFocusRipple
            sx={{
              fontSize: { xs: "14px", md: "16px" },
              fontFamily: "Quicksand-Bold",
              backgroundColor: "#3B82F6",
              "&:hover": {
                backgroundColor: "#2563EB",
                boxShadow: "none !important",
              },
              "&:active": {
                backgroundColor: "#3B82F6",
                boxShadow: "none !important",
              },
              "&:focus": {
                backgroundColor: "#3B82F6",
                boxShadow: "none !important",
              },
              borderRadius: 2,
              width: "100%",
              color: "white",
              textTransform: "none",
              paddingTop: 2,
              paddingBottom: 2,
              boxShadow: "none !important",
              transition: "none !important",
              "& .MuiTouchRipple-root": {
                display: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: "#E5E7EB",
                color: "#9CA3AF",
                boxShadow: "none !important",
              },
            }}
          >
            {isProcessing ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              ` PAGAR $${formatearPrecio(cobro?.valor_cobro)} `
            )}
          </Button>
        </>
      );
    }

    if (estadoCobro === "pagado") {
      return (
        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            fontSize: { xs: "14px", md: "16px" },
            color: "#191C1F",
            textAlign: "left",
            lineHeight: 1.6,
          }}
        >
          Hola{" "}
          <Box component="span" sx={{ fontWeight: "bold" }}>
            {paciente?.nombres} {paciente?.apellidos}
          </Box>
          ,
          <br />
          <br />
          Tu sesión del{" "}
          <Box component="span" sx={{ fontWeight: "bold" }}>
            {formatDate(sesion?.fecha)}
          </Box>{" "}
          a las{" "}
          <Box component="span" sx={{ fontWeight: "bold" }}>
            {sesion?.hora}
          </Box>{" "}
          con tu {profesional?.profesion?.toLowerCase() || "profesional"}{" "}
          <Box component="span" sx={{ fontWeight: "bold" }}>
            {profesional?.nombre}
          </Box>{" "}
          <Box
            component="span"
            sx={{ fontWeight: "bold", fontSize: { xs: "15px", md: "17px" } }}
          >
            YA SE ENCUENTRA PAGADA
          </Box>
          .
          <br />
          <br />
          El pago fue procesado exitosamente el{" "}
          <Box component="span" sx={{ fontWeight: "bold" }}>
            {formatTimestamp(cobro?.timestamp_pago)}
          </Box>{" "}
          por un monto de{" "}
          <Box component="span" sx={{ fontWeight: "bold" }}>
            ${formatearPrecio(cobro?.valor_cobro)}
          </Box>
        </Typography>
      );
    }

    if (estadoCobro === "no_cobrable") {
      const esSuspendida = sesion?.estado_ejecucion === "suspendida";
      const estaReagendada = sesion?.reagendada === true;

      if (esSuspendida && estaReagendada) {
        return (
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              color: "#191C1F",
              textAlign: "left",
              lineHeight: 1.6,
            }}
          >
            Hola{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {paciente?.nombres} {paciente?.apellidos}
            </Box>
            ,
            <br />
            <br />
            Esta sesión del{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {formatDate(sesion?.fecha)}
            </Box>{" "}
            a las{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {sesion?.hora}
            </Box>{" "}
            con tu {profesional?.profesion?.toLowerCase() || "profesional"}{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {profesional?.nombre}
            </Box>{" "}
            fue suspendida y reagendada para el{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {formatDate(sesion?.reagendada_para_fecha)}
            </Box>{" "}
            a las{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {sesion?.reagendada_para_hora}
            </Box>
            . Por favor, contacta a tu profesional para más información sobre tu nueva sesión.
          </Typography>
        );
      }

      if (esSuspendida && !estaReagendada) {
        return (
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              color: "#191C1F",
              textAlign: "left",
              lineHeight: 1.6,
            }}
          >
            Hola{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {paciente?.nombres} {paciente?.apellidos}
            </Box>
            ,
            <br />
            <br />
            Esta sesión del{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {formatDate(sesion?.fecha)}
            </Box>{" "}
            a las{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {sesion?.hora}
            </Box>{" "}
            con tu {profesional?.profesion?.toLowerCase() || "profesional"}{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {profesional?.nombre}
            </Box>{" "}
            fue suspendida. Por favor, contacta a tu profesional para más información.
          </Typography>
        );
      }

      return (
        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            fontSize: { xs: "14px", md: "16px" },
            color: "#191C1F",
            textAlign: "left",
            lineHeight: 1.6,
          }}
        >
          Hola{" "}
          <Box component="span" sx={{ fontWeight: "bold" }}>
            {paciente?.nombres} {paciente?.apellidos}
          </Box>
          ,
          <br />
          <br />
          Esta sesión del{" "}
          <Box component="span" sx={{ fontWeight: "bold" }}>
            {formatDate(sesion?.fecha)}
          </Box>{" "}
          a las{" "}
          <Box component="span" sx={{ fontWeight: "bold" }}>
            {sesion?.hora}
          </Box>{" "}
          con tu {profesional?.profesion?.toLowerCase() || "profesional"}{" "}
          <Box component="span" sx={{ fontWeight: "bold" }}>
            {profesional?.nombre}
          </Box>{" "}
          no está pendiente de pago y no puede ser pagada en este momento.
        </Typography>
      );
    }

    return (
      <Typography
        sx={{
          fontFamily: "Quicksand-Regular",
          fontSize: { xs: "14px", md: "16px" },
          color: "#191C1F",
          textAlign: "center",
        }}
      >
        Estado de sesión no reconocido.
      </Typography>
    );
  };

  return (
    <>
      <LoaderOverlay isLoading={isProcessing} />

      <img
        src={logoImage}
        alt="Clinifica Logo"
        style={{
          width: isMobile ? "150px" : "200px",
          height: "auto",
          paddingTop: "15px",
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
          padding: 15,
        }}
      />

      <Box
        translate="no"
        className="notranslate"
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "#fbfafe",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 2,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
        }}
      >
        {loading ? (
          <>
            <Skeleton animation="wave" height={20} width="60%" />
            <Skeleton animation="wave" height={30} width="80%" />
            <Skeleton animation="wave" height={20} width="50%" />
            <Skeleton animation="wave" height={30} width="70%" />
            <Skeleton animation="wave" height={20} width="40%" />
            <Skeleton animation="wave" height={25} width="30%" />
            <Skeleton animation="wave" height={25} width="50%" />
          </>
        ) : error ? (
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              color: "#d32f2f",
              textAlign: "center",
            }}
          >
            ⚠️ {error}
          </Typography>
        ) : cobroData ? (
          <>
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                color: "#626262",
                fontSize: { xs: "14px", md: "16px" },
                mb: 0,
              }}
            >
              {getTextoEncabezado(cobroData.cobro?.estado_cobro)}
            </Typography>

            <Typography
              sx={{
                mt: -0.5,
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "18px", md: "20px" },
                color: "#191C1F",
                mb: 0,
              }}
            >
              {cobroData.profesional?.nombre}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                mt: -0.5,
                fontSize: { xs: "14px", md: "16px" },
                mb: 0,
              }}
            >
              {cobroData.profesional?.profesion}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                color: "#626262",
                mt: 2,
                fontSize: { xs: "14px", md: "16px" },
                mb: 0,
              }}
            >
              El servicio de:
            </Typography>

            <Typography
              sx={{
                mt: -0.5,
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "18px", md: "20px" },
                color: "#191C1F",
                mb: 0,
              }}
            >
              {cobroData.sesion?.nombre_servicio}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                mt: -0.5,
                fontSize: { md: "16px", xs: "14px" },
                color: "#191C1F",
                mb: 0,
              }}
            >
              ${formatearPrecio(cobroData.cobro?.valor_cobro)}
            </Typography>

            <Box
              sx={{
                display: "inline-block",
                backgroundColor: "#f0f4ff",
                color: "#3B82F6",
                fontFamily: "Quicksand-Medium",
                fontSize: { md: "15px", xs: "13px" },
                borderRadius: 2,
                paddingLeft: 0.75,
                paddingRight: 0.75,
                paddingTop: 0.25,
                paddingBottom: 0.25,
                marginLeft: -0.75,
                mt: -0.75,
                textAlign: "left",
              }}
            >
              {transformModalidad(cobroData.sesion?.modalidad)}
            </Box>

            <Typography
              sx={{
                mt: 0,
                color: "#007FFF",
                fontFamily: "Quicksand-Bold",
                fontSize: { md: "16px", xs: "14px" },
              }}
            >
              {formatDate(cobroData.sesion?.fecha)} - {cobroData.sesion?.hora}
            </Typography>
          </>
        ) : null}
      </Box>

      {!loading && cobroData && (
        <Typography
          sx={{
            fontFamily: "Quicksand-Bold",
            fontSize: { md: "24px", xs: "20px" },
            textAlign: "center",
            color: "#191C1F",
            mt: 3,
            mb: 1,
          }}
        >
          {getTituloPagina(cobroData.cobro?.estado_cobro)}
        </Typography>
      )}

      <Box
        translate="no"
        className="notranslate"
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "white",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 8,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
        }}
      >
        {loading ? (
          <>
            <Skeleton animation="wave" height={20} width="90%" />
            <Skeleton animation="wave" height={20} width="85%" />
            <Skeleton animation="wave" height={20} width="80%" />
            <Skeleton animation="wave" height={50} width="100%" sx={{ mt: 2 }} />
          </>
        ) : error ? (
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              color: "#d32f2f",
              textAlign: "center",
            }}
          >
            No se pudo cargar la información. Por favor, verifica el enlace o contacta con soporte.
          </Typography>
        ) : (
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: "66.67%" },
              mx: "auto",
            }}
          >
            {renderContenidoCajaBlanca()}
          </Box>
        )}
      </Box>
    </>
  );
}
